import { Col, Modal, Row } from 'react-bootstrap';
import { Modal as AntModal } from 'antd';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import saveAs from 'file-saver';
import { createEvent, EventAttributes } from 'ics';
import { RptAgendaCommercialeDto } from '@api/client';
import LinksButton from 'shared/design-system/components/links-button';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import useGetAllActiveUsers from 'shared/_hooks/useGetAllActiveUsers';
import { esitoLabel } from '../../_utils/esitoLabel';

interface Props {
  show: boolean;
  setShow: (show: boolean) => void;
  data: RptAgendaCommercialeDto | undefined;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ESITO_STYLE: Record<string, { bg: string; color: string; icon: string }> = {
  POSITIVO: { bg: '#4caf50', color: '#fff', icon: 'fas fa-trophy' },
  NEGATIVO: { bg: '#ef4444', color: '#fff', icon: 'fas fa-times-circle' },
  NEUTRO: { bg: '#f59e0b', color: '#fff', icon: 'fas fa-comments' },
  ANNULLATA: { bg: '#6c757d', color: '#fff', icon: 'fas fa-ban' },
};

const DetailRow: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => (
  <Col lg={4} md={6} sm={12} xs={12} className="mb-3">
    <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '0.95rem', color: '#333' }}>{value ?? '-'}</div>
  </Col>
);

const AgendaActionDetailModal: React.FC<Props> = ({ show, setShow, data, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const allUsers = useGetAllActiveUsers();

  const esitoStyle = data?.esito ? ESITO_STYLE[data.esito] : undefined;
  const utenteAssegnato = data?.fkUtente != null
    ? allUsers?.find((u) => u.id === data.fkUtente)?.nominativo ?? String(data.fkUtente)
    : undefined;

  const handleDownloadIcs = () => {
    if (!data?.actionDate) {
      AppToastService.error(t('Data azione mancante: impossibile generare l\'evento'));
      return;
    }
    const d = new Date(data.actionDate);
    const cliente = data.nomeAccount || data.clienteId || '';
    const descrizioneParts = [
      data.nominativoCliente && `${t('Referente')}: ${data.nominativoCliente}`,
      data.modalitaContatto && `${t('Modalità contatto')}: ${data.modalitaContatto}`,
      data.denominazioneStrutturaBusiness && `${t('Struttura di business')}: ${data.denominazioneStrutturaBusiness}`,
      data.esito && `${t('Esito')}: ${esitoLabel(data.esito)}`,
      data.amount != null && `${t('Importo (€)')}: ${data.amount}`,
      data.margin != null && `${t('Margine (€)')}: ${data.margin}`,
      data.nota && `${t('Nota')}: ${data.nota}`,
      data.notaEsito && `${t('Nota esito')}: ${data.notaEsito}`,
      utenteAssegnato && `${t('Richiesta supporto a')}: ${utenteAssegnato}`,
    ].filter(Boolean);
    const event: EventAttributes = {
      start: [d.getFullYear(), d.getMonth() + 1, d.getDate(), 9, 0],
      duration: { hours: 1 },
      title: `${t('Attività')} - ${cliente}`,
      description: descrizioneParts.join('\n'),
    };
    createEvent(event, (error, value) => {
      if (error || !value) {
        AppToastService.error(t('Errore nella generazione del file ICS'));
        return;
      }
      const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
      const safeCliente = cliente.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40) || 'attivita';
      const dateStr = format(d, 'yyyy-MM-dd');
      saveAs(blob, `attivita-${safeCliente}-${dateStr}.ics`);
    });
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="title-modal-s" style={{ fontSize: '1em' }}>
          {t('Dettaglio attività')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-end mb-3">
          <span
            role="button"
            onClick={handleDownloadIcs}
            title={t('Scarica file .ics per aggiungere al calendario')}
            className="d-inline-flex align-items-center text-blue-links"
            style={{
              cursor: 'pointer',
              border: '1px solid #304BA2',
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: '0.82rem',
              fontWeight: 600,
              gap: 8,
            }}
          >
            <i className="fa-regular fa-calendar-plus" />
            {t('Scarica file .ics')}
          </span>
        </div>
        <Row>
          <DetailRow label={t('Cliente')} value={data?.nomeAccount || data?.clienteId} />
          <DetailRow label={t('Nominativo cliente')} value={data?.nominativoCliente} />
          <DetailRow
            label={t('Data azione')}
            value={data?.actionDate ? format(new Date(data.actionDate), 'dd/MM/yyyy') : undefined}
          />
          <DetailRow label={t('Modalità contatto')} value={data?.modalitaContatto} />
          <DetailRow
            label={t('Struttura di business')}
            value={data?.denominazioneStrutturaBusiness || data?.strutturaBusinessId}
          />
          <DetailRow label={t('Richiesta supporto a')} value={utenteAssegnato} />
          <Col lg={4} md={6} sm={12} xs={12} className="mb-3">
            <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase' }}>
              {t('Stato attività')}
            </div>
            {data?.statoAttivita ? (
              <span
                style={{
                  backgroundColor: '#eef0f5',
                  color: '#3a3b45',
                  borderRadius: '12px',
                  padding: '3px 12px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  display: 'inline-block',
                }}
              >
                {data.statoAttivita}
              </span>
            ) : (
              <div>-</div>
            )}
          </Col>
        </Row>
        <Row>
          <Col lg={4} md={6} sm={12} xs={12} className="mb-3">
            <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase' }}>
              {t('Esito')}
            </div>
            {esitoStyle ? (
              <span
                style={{
                  backgroundColor: esitoStyle.bg,
                  color: esitoStyle.color,
                  borderRadius: '12px',
                  padding: '3px 12px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <i className={esitoStyle.icon} />
                {esitoLabel(data?.esito)}
              </span>
            ) : (
              <div>-</div>
            )}
          </Col>
          <DetailRow
            label={t('Importo (€)')}
            value={
              data?.amount != null
                ? data.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })
                : undefined
            }
          />
          <DetailRow
            label={t('Margine (€)')}
            value={
              data?.margin != null
                ? data.margin.toLocaleString('it-IT', { minimumFractionDigits: 2 })
                : undefined
            }
          />
        </Row>
        <Row>
          <Col lg={12} className="mb-3">
            <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase' }}>
              {t('Nota')}
            </div>
            <div style={{ fontSize: '0.95rem', color: '#333', whiteSpace: 'pre-wrap' }}>{data?.nota ?? '-'}</div>
          </Col>
        </Row>
        {data?.esito && (
          <Row>
            <Col lg={12} className="mb-3">
              <div className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase' }}>
                {t('Nota esito')}
              </div>
              <div style={{ fontSize: '0.95rem', color: '#333', whiteSpace: 'pre-wrap' }}>{data?.notaEsito ?? '-'}</div>
            </Col>
          </Row>
        )}
        <Row>
          <DetailRow
            label={t('Data creazione')}
            value={data?.creationDate ? format(new Date(data.creationDate), 'dd/MM/yyyy HH:mm') : undefined}
          />
          <DetailRow
            label={t('Ultima modifica')}
            value={data?.lastmodifiedDate ? format(new Date(data.lastmodifiedDate), 'dd/MM/yyyy HH:mm') : undefined}
          />
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {(onEdit || onDelete) && (
          <>
            {onDelete && (
            <LinksButton
              className="btn btn-outline-danger btn-links me-auto"
              onClick={() => {
                AntModal.confirm({
                  title: t('Conferma eliminazione'),
                  content: t('Sei sicuro di voler eliminare questa attività?'),
                  okText: t('Elimina'),
                  okType: 'danger',
                  cancelText: t('common.cancel'),
                  onOk: () => {
                    setShow(false);
                    onDelete();
                  },
                });
              }}
            >
              <i className="icon-delete me-2" />
              {t('Elimina')}
            </LinksButton>
          )}
          <LinksButton className="btn btn-links-outline" onClick={() => setShow(false)}>
            {t('common.cancel')}
          </LinksButton>
            {onEdit && (
              <LinksButton
                className="btn-primary btn-links"
                onClick={() => {
                  setShow(false);
                  onEdit();
                }}
              >
                <i className="icon-edit me-2" />
                {t('Modifica')}
              </LinksButton>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AgendaActionDetailModal;
